@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
@tailwind base;
@tailwind components;
@tailwind utilities;

:root {
  --font-heading: 'Fraunces', Georgia, serif;
  --font-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  --font-label: 'Plus Jakarta Sans', system-ui, sans-serif;

  /* Royal Blue (#2563EB) — primary brand / conversion colour, deepening to navy */
  --primary-50: 0.972 0.018 254;
  --primary-100: 0.938 0.038 255;
  --primary-200: 0.882 0.068 257;
  --primary-300: 0.8 0.108 259;
  --primary-400: 0.678 0.165 261;
  --primary-500: 0.549 0.213 262;
  --primary-600: 0.49 0.205 263;
  --primary-700: 0.415 0.18 264;
  --primary-800: 0.338 0.15 264;
  --primary-900: 0.285 0.1 262;
  --primary-950: 0.245 0.062 258;

  /* Violet (#7C3AED) — second theme: highlights, gradients, editorial accents */
  --accent-50: 0.972 0.014 290;
  --accent-100: 0.94 0.036 291;
  --accent-200: 0.886 0.07 292;
  --accent-300: 0.808 0.114 292;
  --accent-400: 0.678 0.186 293;
  --accent-500: 0.541 0.235 293;
  --accent-600: 0.478 0.226 293;
  --accent-700: 0.405 0.196 293;
  --accent-800: 0.335 0.162 293;
  --accent-900: 0.278 0.128 292;
  --accent-950: 0.215 0.088 291;

  /* Bright Cyan (#06B6D4) into Teal (#14B8A6) — supporting medical colour */
  --secondary-50: 0.984 0.018 200;
  --secondary-100: 0.956 0.042 202;
  --secondary-200: 0.906 0.074 204;
  --secondary-300: 0.838 0.104 206;
  --secondary-400: 0.738 0.128 208;
  --secondary-500: 0.702 0.122 186;
  --secondary-600: 0.604 0.112 186;
  --secondary-700: 0.508 0.096 187;
  --secondary-800: 0.42 0.078 188;
  --secondary-900: 0.342 0.062 189;
  --secondary-950: 0.262 0.05 190;

  /* Near-white cool base canvas with a soft blue tint */
  --background-50: 0.992 0.004 255;
  --background-100: 0.975 0.01 255;
  --background-200: 0.948 0.018 255;
  --background-300: 0.918 0.026 255;
  --background-400: 0.875 0.033 255;
  --background-500: 0.832 0.038 255;
  --background-600: 0.775 0.038 255;
  --background-700: 0.695 0.038 255;
  --background-800: 0.585 0.038 255;
  --background-900: 0.465 0.036 255;
  --background-950: 0.3 0.034 255;

  /* Deep Navy (#102A43) — headings and strongest text */
  --foreground-50: 0.972 0.006 252;
  --foreground-100: 0.93 0.012 252;
  --foreground-200: 0.87 0.02 252;
  --foreground-300: 0.772 0.03 253;
  --foreground-400: 0.65 0.04 254;
  --foreground-500: 0.552 0.046 254;
  --foreground-600: 0.462 0.05 255;
  --foreground-700: 0.375 0.052 256;
  --foreground-800: 0.31 0.052 255;
  --foreground-900: 0.272 0.048 253;
  --foreground-950: 0.24 0.042 252;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: var(--font-body);
  background-color: oklch(var(--background-50));
  color: oklch(var(--foreground-950));
  -webkit-font-smoothing: antialiased;
}

h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-heading);
  font-optical-sizing: auto;
}

/* Token-based gradient text helpers */
.text-gradient-brand {
  background-image: linear-gradient(120deg, oklch(var(--primary-500)), oklch(var(--accent-500)));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.text-gradient-medical {
  background-image: linear-gradient(120deg, oklch(var(--secondary-600)), oklch(var(--secondary-400)));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@keyframes readdy-fade-up {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-up {
  animation: readdy-fade-up 0.6s ease-out both;
}

@keyframes readdy-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.animate-fade-in {
  animation: readdy-fade-in 0.7s ease-out both;
}

@keyframes readdy-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.animate-float {
  animation: readdy-float 6s ease-in-out infinite;
}

@keyframes readdy-float-slow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

.animate-float-slow {
  animation: readdy-float-slow 8s ease-in-out infinite;
}

.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s ease-out, transform 0.7s ease-out;
}

.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Honeypot field — hidden without obvious utility classes */
.form-honeypot-field {
  position: absolute;
  left: -9999px;
  top: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

@media (prefers-reduced-motion: reduce) {
  html { scroll-behavior: auto; }
  .animate-fade-up, .animate-fade-in, .animate-float, .animate-float-slow { animation: none; }
  .reveal { opacity: 1; transform: none; transition: none; }
}